import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import Handlebars from "handlebars";

interface TemplatePreviewProps {
  template: {
    template_name: string;
    sections: any[];
    merge_fields: any[];
  };
}

export function TemplatePreview({ template }: TemplatePreviewProps) {
  // Generate sample data for preview
  const sampleData: Record<string, any> = {
    current_date: new Date().toLocaleDateString(),
    current_year: new Date().getFullYear().toString(),
    una_name: "Sample UNA",
    una_address: "123 Main Street",
    una_city: "Denver",
    una_state: "CO",
    una_zip: "80202",
    treasury_secretary_name: "John Doe",
    treasury_secretary_email: "john@example.com",
  };

  // Add sample data for custom merge fields
  template.merge_fields.forEach((field: any) => {
    if (!sampleData[field.name]) {
      sampleData[field.name] = field.default_value || `[${field.label}]`;
    }
  });

  function renderSection(section: any) {
    try {
      const compiledTemplate = Handlebars.compile(section.content_template || "");
      return compiledTemplate(sampleData);
    } catch (error) {
      return `Error rendering section: ${error}`;
    }
  }

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Live Preview</CardTitle>
        <Button size="sm" variant="outline">
          <RefreshCw className="h-3 w-3 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <div 
          className="border rounded-lg p-8 bg-white min-h-[600px] shadow-sm"
          style={{ 
            fontFamily: 'Cambria, Georgia, serif',
            fontStyle: 'italic',
            fontSize: '12pt',
            lineHeight: '1.6',
            margin: '1in',
            maxWidth: '8.5in'
          }}
        >
          {/* Document Title */}
          <h1 
            className="text-2xl font-bold text-center mb-8"
            style={{ fontFamily: 'Cambria, Georgia, serif', fontStyle: 'italic' }}
          >
            {template.template_name || "Untitled Document"}
          </h1>

          {/* Sections */}
          {template.sections.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No sections to preview. Add sections to see them here.
            </p>
          ) : (
            template.sections.map((section: any, index: number) => (
              <div key={index} className="mb-6">
                <h2 
                  className="text-lg font-semibold mb-3"
                  style={{ fontFamily: 'Cambria, Georgia, serif', fontStyle: 'italic' }}
                >
                  {section.title}
                </h2>
                <div 
                  className="whitespace-pre-wrap"
                  style={{ fontFamily: 'Cambria, Georgia, serif', fontStyle: 'italic' }}
                  dangerouslySetInnerHTML={{ __html: renderSection(section) }}
                />
              </div>
            ))
          )}

          {/* Merge Fields Reference */}
          {template.merge_fields.length > 0 && (
            <div className="mt-8 pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-2">Available merge fields:</p>
              <div className="flex flex-wrap gap-2">
                {template.merge_fields.map((field: any, index: number) => (
                  <code key={index} className="text-xs bg-muted px-2 py-1 rounded">
                    {`{{${field.name}}}`}
                  </code>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
